// ============================================================================
// ProfileEditForm — the client PROFILE editor in a slide-over Drawer.
//   • name (required)            → TextField
//   • sex (m/f/unspecified)      → SelectField (design-system dropdown)
//   • birthday                   → DateField (calendar popup)
//   • height_cm                  → numeric TextField with a "cm" suffix
//   • occupation                 → TextField
//   • remarks                    → textarea (styled to match TextField)
//   • weekly availability        → structured AvailabilityPicker
// Submits via useUpdateClient (optimistic + live-synced). The availability week
// is serialized into the free-text column through the lib codec; legacy plain
// text is surfaced as a notice so it isn't silently overwritten.
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Banner,
  Button,
  DateField,
  PhoneField,
  SelectField,
  TextField,
  isE164,
} from "../../components";
import { useUpdateClient } from "../../hooks/clients";
import { useAccount } from "../../hooks/account";
import type { Client, ClientInput } from "../../api";
import { cn } from "../../lib/cn";
import { currencySymbol, isRateType } from "../../lib/currency";
import { isEmail } from "../../lib/validation";
import {
  ACTIVITY_LEVELS,
  normalizeActivityLevel,
  type ActivityLevelCode,
} from "../clients/lib";
import { Drawer } from "./Drawer";
import { AvailabilityPicker } from "./Availability";
import {
  normalizeSex,
  parseAvailability,
  serializeAvailability,
  type WeeklyAvailability,
} from "./lib";

interface ProfileEditFormProps {
  client: Client;
  open: boolean;
  onClose: () => void;
}

type RateOption = "" | "per_session" | "per_hour";

function normalizeRateType(v: string | null | undefined): RateOption {
  return isRateType(v) ? v : "";
}

export function ProfileEditForm({ client, open, onClose }: ProfileEditFormProps) {
  const { t } = useTranslation("clientDetail");
  const update = useUpdateClient();
  const account = useAccount();
  const symbol = currencySymbol(account.data?.default_currency);

  const parsed = useMemo(
    () => parseAvailability(client.availability),
    [client.availability],
  );

  const [name, setName] = useState(client.name);
  const [phone, setPhone] = useState(client.phone ?? "");
  const [email, setEmail] = useState(client.email ?? "");
  const [sex, setSex] = useState(normalizeSex(client.sex));
  const [birthday, setBirthday] = useState(client.birthday ?? "");
  const [height, setHeight] = useState(
    client.height_cm != null ? String(client.height_cm) : "",
  );
  const [activityLevel, setActivityLevel] = useState<ActivityLevelCode>(
    normalizeActivityLevel(client.activity_level),
  );
  const [occupation, setOccupation] = useState(client.occupation ?? "");
  const [remarks, setRemarks] = useState(client.remarks ?? "");
  const [weekly, setWeekly] = useState<WeeklyAvailability>(parsed.weekly);
  const [rateType, setRateType] = useState<RateOption>(
    normalizeRateType(client.rate_type),
  );
  const [rateAmount, setRateAmount] = useState(
    client.rate_amount != null ? String(client.rate_amount) : "",
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Re-seed the form whenever it (re)opens or the client changes underneath.
  useEffect(() => {
    if (!open) return;
    setName(client.name);
    setPhone(client.phone ?? "");
    setEmail(client.email ?? "");
    setSex(normalizeSex(client.sex));
    setBirthday(client.birthday ?? "");
    setHeight(client.height_cm != null ? String(client.height_cm) : "");
    setActivityLevel(normalizeActivityLevel(client.activity_level));
    setOccupation(client.occupation ?? "");
    setRemarks(client.remarks ?? "");
    setWeekly(parseAvailability(client.availability).weekly);
    setRateType(normalizeRateType(client.rate_type));
    setRateAmount(client.rate_amount != null ? String(client.rate_amount) : "");
    setNameError(null);
    setPhoneError(null);
    setEmailError(null);
  }, [open, client]);

  const submit = () => {
    // Required: name, email (valid), phone (valid E.164). Surface all at once.
    let ok = true;
    if (!name.trim()) {
      setNameError(t("profile.nameRequired"));
      ok = false;
    }
    const emailTrimmed = email.trim();
    if (!emailTrimmed) {
      setEmailError(t("profile.emailRequired"));
      ok = false;
    } else if (!isEmail(emailTrimmed)) {
      setEmailError(t("profile.emailInvalid"));
      ok = false;
    }
    if (!phone.trim()) {
      setPhoneError(t("profile.phoneRequired"));
      ok = false;
    } else if (!isE164(phone)) {
      setPhoneError(t("profile.phoneInvalid"));
      ok = false;
    }
    if (!ok) return;

    const heightNum = height.trim() === "" ? null : Number(height);
    // Charge model: a model only counts when both a type and a (valid) amount
    // are present; otherwise clear both so we never store half a rate.
    const amountNum = rateAmount.trim() === "" ? null : Number(rateAmount);
    const amountValid = amountNum != null && !Number.isNaN(amountNum) && amountNum >= 0;
    const hasRate = rateType !== "" && amountValid;
    const body: ClientInput = {
      name: name.trim(),
      phone: phone.trim(),
      email: emailTrimmed,
      sex: sex || null,
      birthday: birthday || null,
      height_cm: heightNum != null && !Number.isNaN(heightNum) ? heightNum : null,
      activity_level: activityLevel || null,
      occupation: occupation.trim() || null,
      remarks: remarks.trim() || null,
      availability: serializeAvailability(weekly),
      rate_type: hasRate ? rateType : null,
      rate_amount: hasRate ? amountNum : null,
    };
    update.mutate(
      { id: client.id, body },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t("profile.editTitle")}
      subtitle={t("profile.editSubtitle")}
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            {t("actions.cancel")}
          </Button>
          <Button onClick={submit} disabled={update.isPending}>
            {update.isPending ? t("actions.saving") : t("actions.save")}
          </Button>
        </div>
      }
    >
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <TextField
          label={t("profile.name")}
          value={name}
          required
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError(null);
          }}
          error={nameError ?? undefined}
          autoComplete="off"
        />

        <TextField
          label={t("profile.email")}
          value={email}
          required
          type="email"
          inputMode="email"
          placeholder={t("profile.emailPlaceholder")}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
          }}
          error={emailError ?? undefined}
          autoComplete="off"
        />

        <PhoneField
          label={t("profile.phone")}
          countryLabel={t("profile.phoneCountry")}
          value={phone}
          required
          hint={t("profile.phoneHint")}
          onChange={(v) => {
            setPhone(v);
            if (phoneError) setPhoneError(null);
          }}
          error={phoneError ?? undefined}
        />

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <SelectField
            label={t("profile.sex")}
            value={sex}
            onChange={(e) => setSex(e.target.value as typeof sex)}
            options={[
              { value: "", label: t("sex.unspecified") },
              { value: "m", label: t("sex.m") },
              { value: "f", label: t("sex.f") },
            ]}
          />
          <DateField
            label={t("profile.birthday")}
            value={birthday}
            onChange={setBirthday}
          />
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <TextField
            label={t("profile.height")}
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            type="number"
            inputMode="decimal"
            min={0}
            step="0.1"
            suffix="cm"
          />
          <SelectField
            label={t("profile.activityLevel")}
            value={activityLevel}
            onChange={(e) =>
              setActivityLevel(e.target.value as ActivityLevelCode)
            }
            options={[
              { value: "", label: t("activity:unset", "Not set") },
              ...ACTIVITY_LEVELS.map((lvl) => ({
                value: lvl,
                label: t(`activity:level.${lvl}`, lvl),
              })),
            ]}
          />
        </div>

        <TextField
          label={t("profile.occupation")}
          value={occupation}
          onChange={(e) => setOccupation(e.target.value)}
          autoComplete="off"
        />

        {/* Charge model — how this client is billed per session. */}
        <div className="rounded-lg bg-surface-sunken/60 p-4 ring-1 ring-line-soft">
          <p className="font-display text-sm font-bold text-body">
            {t("charge.title")}
          </p>
          <p className="mt-0.5 text-sm text-body-muted">{t("charge.subtitle")}</p>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SelectField
              label={t("charge.model")}
              value={rateType}
              onChange={(e) => setRateType(e.target.value as RateOption)}
              options={[
                { value: "", label: t("charge.none") },
                { value: "per_session", label: t("charge.perSession") },
                { value: "per_hour", label: t("charge.perHour") },
              ]}
            />
            <TextField
              label={
                rateType === "per_hour"
                  ? t("charge.amountPerHour")
                  : t("charge.amount")
              }
              value={rateAmount}
              onChange={(e) => setRateAmount(e.target.value)}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              disabled={rateType === ""}
              suffix={symbol || undefined}
            />
          </div>
        </div>

        <div className="w-full">
          <label
            htmlFor="client-remarks"
            className="mb-1.5 block font-display text-sm font-bold text-body"
          >
            {t("profile.remarks")}
          </label>
          <textarea
            id="client-remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows={3}
            placeholder={t("profile.remarksPlaceholder")}
            className={cn(
              "w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint",
              "ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast",
              "focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none",
            )}
          />
        </div>

        {parsed.legacyText && (
          <Banner tone="info" title={t("availability.legacyTitle")}>
            {t("availability.legacyBlurb", { text: parsed.legacyText })}
          </Banner>
        )}

        <AvailabilityPicker value={weekly} onChange={setWeekly} />

        {update.isError && (
          <Banner tone="danger" title={t("profile.saveError")}>
            {t("profile.saveErrorBlurb")}
          </Banner>
        )}
      </form>
    </Drawer>
  );
}
